import React, { useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { SmartMeterDocument } from 'src/Libs/shop/models/system-configure-checkout-overview.model'
import { SmartMeterCartQuantity } from 'src/Libs/shop/utils/smart-meter'
import { ShopCheckoutSideBarResponse } from 'src/Libs/shop/models'
import useShopSmartMeter from 'src/Libs/shop/hooks/use-shop-smart-meter'
import AddNewSmartMeter from './AddNewSmartMeter'
import EditExistingSmartMeter from './EditExistingSmartMeter'

interface SmartMeterDocumentUploadModalProps {
  smartMeterCartId: string
  documentInfo: SmartMeterDocument[] | null
  smartMeterQuantity: SmartMeterCartQuantity | null
  place: number
  onClose: () => void
  onConfirm?: () => void
  onCartResponse?: (cartData: ShopCheckoutSideBarResponse | null) => void
}

const useStyles = makeStyles()(theme => ({
  quantityLabel: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  smartMeterLabel: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    marginTop: theme.spacing(2),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  divider: {
    width: '2px',
    backgroundColor: theme.palette.grey[300],
    height: 'auto',
    alignSelf: 'stretch',
    margin: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  smartMeterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
}))

/*
 * This component renders a modal for smart meter upload or edit
 */
const SmartMeterDocumentUploadModal: React.FC<SmartMeterDocumentUploadModalProps> = ({
  smartMeterCartId,
  documentInfo,
  smartMeterQuantity,
  place,
  onClose,
  onConfirm,
  onCartResponse,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<SmartMeterDocument[] | null>(documentInfo)
  const { isLoading } = useShopSmartMeter()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const uploadedFilesCount = uploadedFiles ? Object.keys(uploadedFiles).length : 0

  const modalActions = {
    primary: {
      name: t('shop.common.further'),
      disabled: isLoading,
      onClick: () => {
        handleClose()
      },
    },
  }

  const handleClose = (): void => {
    onClose()
    if (onConfirm) {
      onConfirm()
    }
  }

  const handleUploadedFiles = (uploadedFiles: SmartMeterDocument[] | null): void => {
    setUploadedFiles(uploadedFiles)
  }

  const getSmartMeterDocumentsCountMessage = (uploadedFilesCount: number, smartMeterQuantity: number): string | null => {
    if (uploadedFilesCount === 0) {
      return t('shop.smart-meter.at-least-a-document')
    }
    if (uploadedFilesCount < smartMeterQuantity) {
      return t('shop.smart-meter.less-documents')
    }
    if (uploadedFilesCount > smartMeterQuantity) {
      return t('shop.smart-meter.more-documents')
    }

    return null
  }

  return (
    <Dialog title={t('shop.smart-meter.shop-smart-meter-dialog-label')} maxWidth={'md'} onClose={handleClose} place={place} actions={modalActions}>
      <Box className={classes.container}>
        <Typography variant="body1" className={classes.smartMeterLabel}>
          {t('shop.smart-meter.selected-files-label')}
        </Typography>

        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <Loading />
          </Box>
        ) : (
          <Box className={classes.smartMeterContainer}>
            <AddNewSmartMeter onCartResponse={onCartResponse} smartMeterCartId={smartMeterCartId} handleUploadedFiles={handleUploadedFiles} />
            <Box className={classes.divider} />
            <EditExistingSmartMeter
              onCartResponse={onCartResponse}
              smartMeterCartId={smartMeterCartId}
              uploadedFiles={uploadedFiles}
              handleUploadedFiles={handleUploadedFiles}
            />
          </Box>
        )}
        {smartMeterQuantity && (
          <Typography className={classes.quantityLabel}>
            {getSmartMeterDocumentsCountMessage(uploadedFilesCount, smartMeterQuantity.smartMeterQuantity)}
          </Typography>
        )}
      </Box>
    </Dialog>
  )
}

export default SmartMeterDocumentUploadModal
